import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Empty, Form, Input, Modal, Radio, Row, Segmented, Spin, Tag, Tooltip, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import { cartService } from '../../services/cartService';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import tmService from '../../services/tmService';
import { errMsg, fmtNum, toastErr } from './components/common';

const { Text } = Typography;

const CYCLE_LABEL = { monthly: '月付', quarterly: '季付', semi_annually: '半年付', annually: '年付', onetime: '免费' };
const CYCLE_UNIT = { monthly: '月', quarterly: '季', semi_annually: '半年', annually: '年' };
const CYCLE_ORDER = ['monthly', 'quarterly', 'semi_annually', 'annually', 'onetime'];

const cyclesOf = (p) => (p.pricing || []).filter((x) => x.enabled)
    .sort((a, b) => CYCLE_ORDER.indexOf(a.billing_cycle) - CYCLE_ORDER.indexOf(b.billing_cycle));
const isFree = (p) => cyclesOf(p).every((x) => Number(x.price) === 0);

/** 新建发信流：选套餐 → 名称/类型 → 加入购物车（付款后开通） */
const NewStreamModal = ({ open, onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [planId, setPlanId] = useState(null);
    const [cycle, setCycle] = useState('monthly');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        tmService.listPlans()
            .then((list) => {
                setPlans(list);
                if (!planId && list.length) setPlanId(list[0].id);
            })
            .catch((e) => toastErr(e, t('Failed to load plans')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const plan = useMemo(() => plans.find((p) => p.id === planId), [plans, planId]);
    const planCycles = plan ? cyclesOf(plan) : [];
    const marketingOk = !!plan?.features?.tm_marketing;
    const type = Form.useWatch('type', form);

    useEffect(() => {
        if (!plan) return;
        const cs = cyclesOf(plan).map((x) => x.billing_cycle);
        if (!cs.includes(cycle)) setCycle(cs[0] || 'monthly');
        if (!plan.features?.tm_marketing && form.getFieldValue('type') === 'marketing') form.setFieldsValue({ type: 'transactional' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planId, plans]);

    const priceText = (p) => {
        if (isFree(p)) return t('Free');
        const row = cyclesOf(p).find((x) => x.billing_cycle === 'monthly') || cyclesOf(p)[0];
        if (!row) return '-';
        return `${formatMoney(Number(row.price), row.currency || PLATFORM_CURRENCY, { decimals: 0 })}/${t(CYCLE_UNIT[row.billing_cycle] || row.billing_cycle)}`;
    };

    const submit = async () => {
        const v = await form.validateFields();
        if (!plan) return;
        setSubmitting(true);
        try {
            const r = await cartService.addItem({
                product_type: 'tm', product_id: plan.id, billing_cycle: cycle,
                config: { name: v.name.trim(), type: v.type, ...(v.type === 'marketing' ? { postal_address: v.postal_address.trim() } : {}) },
            });
            if (r && r.success === false) {
                message.error(r.error || r.message || t('Failed to add to cart'));
                return;
            }
            window.dispatchEvent(new CustomEvent('cart:updated'));
            onClose && onClose();
            navigate(userPath('/billing/cart'));
        } catch (e) {
            // cartService 抛出的是响应体；带 error 的已由 apiClient 弹过
            if (!e?.error) message.error(errMsg(e, t('Failed to add to cart')));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onCancel={onClose} title={t('New stream')} width={720} okText={t('Add to cart')}
            onOk={submit} confirmLoading={submitting} okButtonProps={{ disabled: !plan }} destroyOnClose>
            <Spin spinning={loading}>
                {!loading && plans.length === 0 ? <Empty description={t('No plans available')} /> : (
                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                        {plans.map((p) => {
                            const f = p.features || {};
                            const emails = p.periodic_usage?.emails?.value;
                            const over = p.periodic_usage?.emails?.overage;
                            const active = p.id === planId;
                            return (
                                <Col xs={12} md={6} key={p.id}>
                                    <Card size="small" hoverable onClick={() => setPlanId(p.id)}
                                        style={{ height: '100%', borderColor: active ? 'var(--ant-color-primary, #2357ff)' : undefined, borderWidth: active ? 2 : 1 }}>
                                        <Text strong>{t(p.name)}</Text>
                                        <div style={{ fontSize: 18, fontWeight: 600, margin: '4px 0' }}>{priceText(p)}</div>
                                        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                                            {isFree(p) ? (
                                                // 免费版额度按账号信用等级（Lv1 100 → Lv6 3000 封/月），不是套餐写死的数
                                                <div>{t('100–3,000 emails/month by credit level')}</div>
                                            ) : (
                                                <>
                                                    <div>{emails === -1 ? t('Unlimited') : t('{{n}} emails/month', { n: fmtNum(emails) })}</div>
                                                    <div><Text type="secondary">{t('{{n}} emails/hour', { n: fmtNum(f.tm_hourly_limit) })}</Text></div>
                                                </>
                                            )}
                                            <div><Text type="secondary">{f.tm_domains_limit === -1 ? t('Unlimited domains') : t('{{n}} domains', { n: f.tm_domains_limit })}</Text></div>
                                            {over?.unit_price ? <div><Text type="secondary">{t('Overage {{p}} per 1,000', { p: formatMoney(over.unit_price, over.currency || PLATFORM_CURRENCY) })}</Text></div> : null}
                                            {f.tm_marketing ? <Tag color="purple" style={{ marginTop: 4 }}>{t('Marketing allowed')}</Tag> : null}
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}
                {plan && isFree(plan) && (
                    <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('The free plan\'s allowance depends on your account credit level. You start in the sandbox, which lifts automatically at Lv2.')} />
                )}
                <Form form={form} layout="vertical" initialValues={{ type: 'transactional' }}>
                    <Form.Item name="name" label={t('Name')}
                        rules={[{ required: true, message: t('Enter a name') }, { pattern: /^[\p{L}\p{N} ._-]{1,64}$/u, message: t('1–64 characters (letters, digits, spaces, . _ -)') }]}>
                        <Input placeholder="my-app" maxLength={64} />
                    </Form.Item>
                    <Form.Item name="type" label={t('Type')}>
                        <Radio.Group>
                            <Radio value="transactional">{t('Transactional')}</Radio>
                            <Tooltip title={marketingOk ? t('Marketing emails always carry an unsubscribe link and the sender\'s postal address') : t('This plan doesn\'t include marketing email')}>
                                <Radio value="marketing" disabled={!marketingOk}>{t('Marketing')}</Radio>
                            </Tooltip>
                        </Radio.Group>
                    </Form.Item>
                    {type === 'marketing' && (
                        <Form.Item name="postal_address" label={t('Sender postal address')} tooltip={t('Added automatically to the footer of marketing emails')}
                            rules={[{ required: true, whitespace: true, message: t('Enter the sender\'s postal address') }, { max: 500 }]}>
                            <Input.TextArea rows={2} maxLength={500} />
                        </Form.Item>
                    )}
                    {planCycles.length > 1 && (
                        <Form.Item label={t('Billing cycle')}>
                            <Segmented value={cycle} onChange={setCycle}
                                options={planCycles.map((x) => ({ value: x.billing_cycle, label: `${t(CYCLE_LABEL[x.billing_cycle] || x.billing_cycle)} ${formatMoney(Number(x.price), x.currency || PLATFORM_CURRENCY, { decimals: 0 })}` }))} />
                        </Form.Item>
                    )}
                </Form>
            </Spin>
        </Modal>
    );
};

export default NewStreamModal;
